const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const handleize = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const updateCartBubble = (itemCount) => {
  const cartBubble = document.getElementById("CartBubble");
  if (!cartBubble) return;

  if (itemCount) {
    cartBubble.classList.remove("hidden");
    cartBubble.textContent = itemCount;
  } else {
    cartBubble.classList.add("hidden");
    cartBubble.textContent = "";
  }
}

// Size selector
class PfSizeSelector extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.sizeInputs.forEach(input => {
      input.addEventListener("change", e => this.selectSize(e.target), { signal });
    });

    this.checkDefaultSize();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get sizeInputs() {
    return this.querySelectorAll("input[type='radio']");
  }

  checkDefaultSize() {
    const inputs = [...this.sizeInputs];

    let target = inputs.find(i => i.checked);

    if (!target && this.dataset.defaultVariant) {
      target = inputs.find(i => i.value === this.dataset.defaultVariant);

      if (!target || Number(target.dataset.quantity) <= 0) {
        target = inputs.find(i => Number(i.dataset.quantity) > 0) || null;
      }
    }

    if (target) this.selectSize(target);
  }

  selectSize(input) {
    input.checked = true;

    const size = {
      name: input.dataset.size,
      variant_id: input.value,
      quantity: input.dataset.quantity
    };

    this.selectedSize = size;
    this.dataset.selected = true;

    this.dispatchEvent(new CustomEvent("sizeselect", {
      detail: size,
      bubbles: true,
      composed: true
    }));
  }
}

if (!customElements.get("pf-size-selector")) {
  customElements.define("pf-size-selector", PfSizeSelector);
}

// Bundle selector
class PfBundleSelector extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.activeItem = this.querySelector('.pf-active');
    this.regularPrice = this.dataset.regularPrice;

    this.#bindEvents();

    if (this.activeItem) {
      const bundle = this.selectedBundle;
      this.#updateHeaderPrice(bundle, true);
      this.#updateElementData(bundle);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get selectedBundle() {
    return this.activeItem ? this.#extractBundleData(this.activeItem) : null;
  }

  set selectedBundle(element) {
    if (!(element instanceof HTMLElement)) return;

    this.#setActiveItem(element);
  }

  get quantity() {
    const quantityEl = document.querySelector("pf-purchase-controls input.pf-quantity-count");
    return parseInt(quantityEl?.value || 1);
  }

  get regularPriceEl() {
    return document.querySelector(".pf-prodinfo-price .pf-price-regular");
  }

  #bindEvents() {
    this.items.forEach(item => {
      item.addEventListener(
        'click',
        () => this.selectedBundle = item,
        { signal: this.#controller.signal }
      );
    });

    document.addEventListener(
      'quantitychange',
      (e) => this.#onQuantityChange(e.detail.quantity, e.detail.manual),
      { signal: this.#controller.signal }
    );
  }

  #setActiveItem(newActive) {
    this.items.forEach(item => item.classList.remove('pf-active'));
    newActive.classList.add('pf-active');

    this.activeItem = newActive;
    const bundle = this.selectedBundle;

    this.#updateHeaderPrice(bundle, false);
    this.#updateElementData(bundle);

    this.dispatchEvent(new CustomEvent('bundlechange', {
      detail: bundle,
      bubbles: true,
      composed: true
    }));
  }

  #extractBundleData(item) {
    return {
      title: item.querySelector('.pf-bundle-title')?.textContent.trim(),
      unitPrice: parseFloat(item.dataset.unitPrice),
      minQty: parseInt(item.dataset.minimumQuantity, 10),
      element: item
    };
  }

  #updateHeaderPrice(bundle, initialUpdate) {
    const discountPrice = document.querySelector(".pf-price-discount");
    if (!discountPrice) return;

    if (bundle.minQty === 1) {
      discountPrice.innerHTML = "";
    } else {
      const amount = bundle.unitPrice % 1 == 0 ? bundle.unitPrice : bundle.unitPrice.toFixed(2);
      discountPrice.innerHTML = `<span class="pf-price-amount">$${amount}</span><span class="pf-price-each">/ each</span>`;
    }

    if(this.regularPriceEl) {
      setTimeout(() => this.regularPriceEl.dataset.quantity = this.quantity, 20);
    }
  }

  #updateElementData(bundle) {
    if (!bundle) return;

    this.dataset.selectedTitle = bundle.title || "";
    this.dataset.selectedUnitPrice = bundle.unitPrice || "";
    this.dataset.selectedMinQty = bundle.minQty || "";
  }

  #onQuantityChange(qty, manual = false) {
    if(this.regularPriceEl) {
      this.regularPriceEl.dataset.quantity = this.quantity;
    }

    if (!manual) return;
    let closestBundle = null;
    let closestDiff = Infinity;

    for (const item of this.items) {
      const minQty = parseInt(item.dataset.minimumQuantity, 10);

      if (qty >= minQty && qty - minQty < closestDiff) {
        closestBundle = item;
        closestDiff = qty - minQty;
      }
    }

    if (closestBundle && closestBundle !== this.activeItem) {
      this.selectedBundle = closestBundle;
    }
  }
}

if (!customElements.get('pf-bundle-selector')) {
  customElements.define('pf-bundle-selector', PfBundleSelector);
}

// Purchase controls
class PfPurchaseControls extends HTMLElement {
  #controller = new AbortController();
  #isManualChange = false;

  connectedCallback() {
    this.isSubscriptions = this.dataset.isSubscriptions == "true";
    this.qtyInput = this.querySelector('[qty-count]');
    this.decrementBtn = this.querySelector('[qty-decrement]');
    this.incrementBtn = this.querySelector('[qty-increment]');
    this.cartBtn = this.querySelector(".pf-cartbtn-trigger");
    this.bundleSelector = document.querySelector('pf-bundle-selector');

    this.updatePrice();

    this.#bindEvents();
    if (this.bundleSelector?.selectedBundle)
      this.#setQuantity(this.bundleSelector.selectedBundle.minQty);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  updatePrice() {
    const bundleExists = document.querySelector("pf-bundle-selector");
    if(bundleExists) return;

    const discountPriceEl = document.querySelector(".pf-prodinfo-price .pf-price-discount");
    const regularPriceEl = document.querySelector(".pf-prodinfo-price .pf-price-regular");

    if(regularPriceEl.dataset.value == discountPriceEl.dataset.value || !regularPriceEl.dataset.value || !discountPriceEl.dataset.value) return;

    regularPriceEl.textContent = regularPriceEl.dataset.value;
    discountPriceEl.textContent = discountPriceEl.dataset.value;
  }

  #bindEvents() {
    this.decrementBtn?.addEventListener(
      'click',
      () => this.#onManualQuantityChange(-1),
      { signal: this.#controller.signal }
    );

    this.incrementBtn?.addEventListener(
      'click',
      () => this.#onManualQuantityChange(1),
      { signal: this.#controller.signal }
    );

    this.qtyInput?.addEventListener(
      'input',
      (e) => this.#onManualInput(e),
      { signal: this.#controller.signal }
    );

    this.bundleSelector?.addEventListener(
      'bundlechange',
      (e) => this.#onBundleChange(e.detail),
      { signal: this.#controller.signal }
    );

    this.cartBtn?.addEventListener(
      'click',
      (e) => this.#onAddToCart(e),
      { signal: this.#controller.signal }
    );

    document.addEventListener(
      "sizeselect",
      (e) => this.#onSizeSelect(e.detail),
      { signal: this.#controller.signal }
    );
  }

  #onManualQuantityChange(delta) {
    this.#isManualChange = true;
    const newQty = Math.max(1, this.#getQuantity() + delta);

    this.#setQuantity(newQty);
    this.#dispatchQuantityChange(newQty, true);
    this.#resetManualFlag();
  }

  #onManualInput(e) {
    this.#isManualChange = true;

    const value = e.target.value.replace(/\D/g, '');
    const num = parseInt(value || '1', 10);
    const validQty = Math.max(1, num);

    e.target.value = validQty;

    this.#dispatchQuantityChange(validQty, true);
    this.#resetManualFlag();
  }

  #onBundleChange(bundle) {
    if (this.#isManualChange) return;
    if (!bundle) return;

    const minQty = parseInt(bundle.minQty, 10) || 1;
    this.#setQuantity(minQty);
  }

  #onSizeSelect(size) {
    const avlQty = Number(size.quantity || 0);

    if(avlQty > 0) {
      this.cartBtn.querySelector("span").innerHTML = "Add to Cart";
      this.cartBtn.setAttribute("size-selected", "");
    } else {
      this.cartBtn.querySelector("span").innerHTML = "Sold Out";
      this.cartBtn.removeAttribute("size-selected");
    }
  }

  #dispatchQuantityChange(qty, manual = false) {
    this.dispatchEvent(new CustomEvent('quantitychange', {
      detail: { quantity: qty, manual },
      bubbles: true,
      composed: true
    }));
  }

  async #onAddToCart(e) {
    e.preventDefault();

    let cartItems = [];
    const colorMixMode = document.querySelector(".pf-colmix-modes")?.dataset.active || "single";
    const quantity = this.#getQuantity();

    if (this.isSubscriptions || colorMixMode === "single") {
      const selectedVariant = document.querySelector(".pf-size-input:checked")?.value;
      const selling_plan = this.isSubscriptions ? document.querySelector(".rc-selling-plans-dropdown__select")?.value : null;

      const item = {
        id: Number(selectedVariant),
        quantity
      };

      if(selling_plan) {
        item.selling_plan = selling_plan;
      }

      if (selectedVariant) {
        cartItems.push(item);
      }
    } else {
      const colorMixer = document.querySelector("pf-color-mixer");
      if (!colorMixer) return;

      const selectedProducts = colorMixer.selectedVariants?.slice(0, quantity) || [];

      for (const curr of selectedProducts) {
        if (!curr || !curr.variantId || curr.quantity < 1) continue;

        const existingItem = cartItems.find(p => p.id === curr.variantId);

        if (existingItem) {
          existingItem.quantity += 1
        } else {
          cartItems.push({ id: curr.variantId, quantity: 1 })
        };
      }
    }

    if (!cartItems.length) {
      console.warn("No variants selected to add to cart.");
      return;
    }

    try {
      this.cartBtn?.classList.add("pf-loading");

      const response = await fetch("/cart/add.js?sections=cart-bubble", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ items: cartItems }),
      });

      const data = await response.json();

      this.cartBtn?.classList.remove("pf-loading");

      if (!response.ok) {
        console.error("Shopify Add to Cart Error:", data);
        return;
      }

      updateCartBubble(data.sections["cart-bubble"].match(/\d+/));

      document.documentElement.dispatchEvent(
        new CustomEvent("product:added-to-cart", { bubbles: true })
      );

      await sleep(250);

      document.querySelector("cart-modal").show();
    } catch (error) {
      this.cartBtn?.classList.remove("pf-loading");

      console.error("Network or fetch error:", error);
    }
  }

  #resetManualFlag() {
    queueMicrotask(() => (this.#isManualChange = false));
  }

  #getQuantity() {
    return parseInt(this.qtyInput?.value || '1', 10);
  }

  #setQuantity(value) {
    if (this.qtyInput) this.qtyInput.value = Math.max(1, parseInt(value, 10) || 1);
  }
}

if (!customElements.get('pf-purchase-controls')) {
  customElements.define('pf-purchase-controls', PfPurchaseControls);
}


// Color mixer
class PfColorMixer extends HTMLElement {
  #controller = new AbortController();
  #productsPromise = null;
  products = null;
  flatProducts = [];
  selectedVariants = [];
  colorCounts = {};

  connectedCallback() {
    const { signal } = this.#controller;

    this.#productsPromise = this.fetchProductsData();

    this.renderVariantTiles();
    this.colorModeWrapper?.addEventListener("click", this.toggleColorMode.bind(this), { signal });
    this.gridWrapper?.addEventListener("click", this.onStepperClick.bind(this), { signal });
    this.gridWrapper?.addEventListener("scroll", this.updateArrows.bind(this), { signal, passive: true });
    this.prevBtn?.addEventListener("click", () => this.scrollGrid(-1), { signal });
    this.nextBtn?.addEventListener("click", () => this.scrollGrid(1), { signal });
    window.addEventListener("resize", this.updateArrows.bind(this), { signal });

    document.addEventListener("quantitychange", this.resetBundleRender.bind(this), { signal });
    document.addEventListener("bundlechange", this.resetBundleRender.bind(this), { signal });
    document.addEventListener("sizeselect", this.renderColorPicker.bind(this), { signal });
    document.addEventListener("product:added-to-cart", this.resetColorMixer.bind(this), { signal });

    if(this.isMixedTabDefault) {
      const bundleSelector = document.querySelector("pf-bundle-selector");
      if(!bundleSelector) return;

      if(parseInt(bundleSelector.dataset.selectedMinQty || 1) > 1) {
        this.colorModeWrapper.setAttribute("data-active", "mixed");
      }
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get colorModeWrapper() {
    return this.querySelector("[data-color-modes]");
  }

  get gridWrapper() {
    return this.querySelector("[data-colmix-grid]");
  }

  get summaryEl() {
    return this.querySelector("[data-colmix-summary]");
  }

  get carouselWrapper() {
    return this.querySelector("[data-colmix-carousel]");
  }

  get prevBtn() {
    return this.querySelector("[data-colmix-prev]");
  }

  get nextBtn() {
    return this.querySelector("[data-colmix-next]");
  }

  get variantPicker() {
    return document.querySelector("product-variant-picker");
  }

  get quantity() {
    const quantityEl = document.querySelector("pf-purchase-controls input.pf-quantity-count");
    return parseInt(quantityEl?.value || 1);
  }

  get mixerMode() {
    const modes = {
      reset_mixer: "none",
      color_only: "color",
      color_and_size: "both"
    };

    return modes[this.dataset.mixerMode];
  }

  get isMixedTabDefault() {
    return this.dataset.mixedTabDefault == "true";
  }

  get totalSelected() {
    return Object.values(this.colorCounts).reduce((sum, n) => sum + n, 0);
  }

  async fetchProductsData() {
    await customElements.whenDefined("product-variant-picker");
    if (!this.variantPicker) return null;

    const data = await this.variantPicker.getProductData();
    this.products = data;

    return data;
  }

  async getProducts() {
    if (this.products) return this.products;

    if (!this.#productsPromise) {
      this.#productsPromise = this.fetchProductsData();
    }

    return this.#productsPromise;
  }

  toggleColorMode(e) {
    const targetBtn = e.target.closest(".pf-colmix-toggle");
    if(!targetBtn || this.colorModeWrapper.dataset.active == targetBtn.dataset.mode) return;

    this.colorModeWrapper.setAttribute("data-active", targetBtn.dataset.mode);

    this.updateAtcButton();
  }

  async renderColorPicker(e) {
    const currentProductId = Number(this.dataset.productId);

    await this.getProducts();

    this.flatProducts = this.products.map(product => {
      const currentVariant = product.variants.find(v => {
        return v.options[1] == e.detail?.name;
      });

      const newProduct = {
        handle: product.handle,
        productId: product.id,
        color: currentVariant?.options[0],
        variantId: currentVariant?.id || null,
        variantName: currentVariant?.id ? e.detail?.name : null,
        quantity: currentVariant?.quantity || 0,
        image: currentVariant?.featured_image?.src || product.featured_image,
        pattern: product.pattern || null
      }

      return newProduct;
    });

    this.flatProducts.sort((a, b) => {
      if (a.productId === currentProductId) return -1;
      if (b.productId === currentProductId) return 1;

      const order = p =>
        p.pattern === "solid" ? 0 :
        p.pattern === "pattern" ? 1 :
        2;

      return order(a) - order(b);
    });

    // Drop stale selections for colours that no longer exist on this product
    // at all; keep ones that simply have no stock in the newly-selected size
    // so they can still surface as "unavailable" rows the user must clear.
    const allVariantIds = new Set(this.flatProducts.map(p => p.variantId));
    Object.keys(this.colorCounts).forEach(id => {
      if (!allVariantIds.has(Number(id))) delete this.colorCounts[id];
    });

    if(this.mixerMode == "none") {
      this.colorCounts = {};
    }

    if(Object.keys(this.colorCounts).length === 0 && this.mixerMode != "none") {
      const first = this.flatProducts.find(p => p.quantity > 0);
      if(first) this.colorCounts[first.variantId] = 1;
    }

    this.renderVariantTiles();
  }

  toggleCartBtn(disabled = true, btnText = "Add to Cart") {
    const cartBtn = document.querySelector(".pf-cartbtn-trigger");
    if(!cartBtn) return;

    cartBtn.toggleAttribute("disabled", disabled);

    if(!cartBtn.hasAttribute("size-selected")) {
      btnText = "Select Size";
    }

    cartBtn.querySelector("span").innerText = btnText;
  }

  updateAtcButton() {
    const colorMixMode = document.querySelector(".pf-colmix-modes")?.dataset.active || "single";
    const selectedCount = this.totalSelected;

    if(this.quantity > selectedCount && colorMixMode == "mixed") {
      this.toggleCartBtn(true, `Select items (${selectedCount}/${this.quantity})`);
    } else {
      this.toggleCartBtn(false);
    }
  }

  buildSelectedVariantsFromCounts() {
    const variants = [];

    Object.entries(this.colorCounts).forEach(([variantId, count]) => {
      const product = this.flatProducts.find(p => p.variantId == variantId);
      if(!product) return;

      for(let i = 0; i < count; i++) variants.push(product);
    });

    this.selectedVariants = variants;
  }

  renderVariantTiles() {
    this.toggleAttribute("data-hidden", Math.max(parseInt(this.quantity), 1) === 1);

    this.buildSelectedVariantsFromCounts();

    const selectedIds = Object.keys(this.colorCounts).map(Number);
    const rowProducts = this.flatProducts.filter(p => p.quantity > 0 || selectedIds.includes(p.variantId));

    let rows = "";

    rowProducts.forEach((product) => {
      const count = this.colorCounts[product.variantId] || 0;
      const isUnavailable = this.mixerMode == "color" && product.quantity <= 0;
      const otherTotal = this.totalSelected - count;
      const remaining = Math.max(this.quantity - otherTotal, 0);
      const maxForRow = isUnavailable ? count : Math.min(product.quantity, remaining);

      const minusDisabled = count === 0;
      const plusDisabled = isUnavailable ? true : count >= maxForRow;

      rows += `
        <div class="pf-colmix-row ${isUnavailable ? "pf-unavailable" : ""}">
          <div class="pf-colmix-row-swatch" style="background-image: url('${product.image}')"></div>
          <div class="pf-colmix-row-label">${product.color || ""}</div>
          ${isUnavailable ? '<span class="pf-colmix-row-flag">Unavailable</span>' : ""}
          <div class="pf-colmix-stepper" data-variant-id="${product.variantId}">
            <button type="button" class="pf-colmix-step pf-colmix-step-minus" ${minusDisabled ? "disabled" : ""} aria-label="Remove one ${product.color || ""}">&minus;</button>
            <span class="pf-colmix-step-count">${count}</span>
            <button type="button" class="pf-colmix-step pf-colmix-step-plus" ${plusDisabled ? "disabled" : ""} aria-label="Add one ${product.color || ""}">+</button>
          </div>
        </div>
      `;
    });

    this.gridWrapper.innerHTML = rows;

    this.renderSummary();
    this.updateAtcButton();
    requestAnimationFrame(() => this.updateArrows());
  }

  updateArrows() {
    const grid = this.gridWrapper;
    if(!grid) return;

    const hasOverflow = grid.scrollWidth > grid.clientWidth + 2;
    const atStart = grid.scrollLeft <= 2;
    const atEnd = grid.scrollLeft >= grid.scrollWidth - grid.clientWidth - 2;

    this.carouselWrapper?.toggleAttribute("data-overflowing", hasOverflow);

    if(this.prevBtn) this.prevBtn.hidden = !hasOverflow || atStart;
    if(this.nextBtn) this.nextBtn.hidden = !hasOverflow || atEnd;
  }

  scrollGrid(direction) {
    const grid = this.gridWrapper;
    if(!grid) return;

    const step = grid.clientWidth * 0.9;
    grid.scrollBy({ left: direction * step, behavior: "smooth" });
  }

  renderSummary() {
    if(!this.summaryEl) return;

    const parts = Object.entries(this.colorCounts)
      .filter(([, count]) => count > 0)
      .map(([variantId, count]) => {
        const product = this.flatProducts.find(p => p.variantId == variantId);
        return product ? `${product.color} (${count})` : null;
      })
      .filter(Boolean);

    this.summaryEl.innerHTML = parts.length
      ? `Colour: ${parts.join(" | ")}`
      : "Select your colours below";
  }

  onStepperClick(e) {
    const btn = e.target.closest(".pf-colmix-step");
    if(!btn || btn.disabled) return;

    const wrapper = btn.closest(".pf-colmix-stepper");
    if(!wrapper) return;

    const variantId = wrapper.dataset.variantId;
    const delta = btn.classList.contains("pf-colmix-step-plus") ? 1 : -1;
    const current = this.colorCounts[variantId] || 0;

    const otherTotal = this.totalSelected - current;
    const maxAllowed = Math.max(this.quantity - otherTotal, 0);
    const newValue = Math.min(Math.max(current + delta, 0), maxAllowed);

    if(newValue > 0) {
      this.colorCounts[variantId] = newValue;
    } else {
      delete this.colorCounts[variantId];
    }

    this.renderVariantTiles();
  }

  resetBundleRender() {
    // Bundle size / quantity changed. Keep as much of the current colour
    // spread as still fits within the new total, trimming the newest
    // selections first if it no longer fits.
    const total = this.totalSelected;

    if(total > this.quantity) {
      const entries = Object.entries(this.colorCounts);
      let excess = total - this.quantity;

      for(let i = entries.length - 1; i >= 0 && excess > 0; i--) {
        const [variantId, count] = entries[i];
        const reduceBy = Math.min(count, excess);
        const newCount = count - reduceBy;

        if(newCount > 0) this.colorCounts[variantId] = newCount;
        else delete this.colorCounts[variantId];

        excess -= reduceBy;
      }
    }

    if(this.totalSelected === 0 && this.mixerMode != "none") {
      const first = this.flatProducts.find(p => p.quantity > 0);
      if(first) this.colorCounts[first.variantId] = 1;
    }

    this.renderVariantTiles();
  }

  resetColorMixer() {
    const first = this.flatProducts.find(p => p.quantity > 0);
    this.colorCounts = first ? { [first.variantId]: 1 } : {};

    this.renderVariantTiles();

    this.colorModeWrapper.dataset.active = "single";
  }
}

if(!customElements.get("pf-color-mixer")) {
  customElements.define("pf-color-mixer", PfColorMixer);
}

// Pf Purchase Together
class PfPurchaseTogether extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.buttons = this.querySelectorAll(".pf-product-cartbtn");
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #bindEvents() {
    this.buttons.forEach(btn => {
      btn.addEventListener(
        "click",
        (e) => this.#onAddToCart(e),
        { signal: this.#controller.signal }
      );
    });
  }

  async #onAddToCart(e) {
    const btn = e.currentTarget;
    const variantId = Number(btn.dataset.variantId);
    const unavailable = btn.hasAttribute("data-unavailable");

    if (unavailable || !variantId) return;

    btn.classList.add("pf-loading");

    try {
      const response = await fetch("/cart/add.js?sections=cart-bubble", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        }),
      });

      const data = await response.json();
      btn.classList.remove("pf-loading");

      if (!response.ok) {
        console.error("Frequently Purchased Add to Cart Error:", data);
        return;
      }

      updateCartBubble(data.sections["cart-bubble"].match(/\d+/));

      document.documentElement.dispatchEvent(
        new CustomEvent("product:added-to-cart", { bubbles: true })
      );

      await sleep(250);

      document.querySelector("cart-modal")?.show();

    } catch (error) {
      btn.classList.remove("pf-loading");
      console.error("Network or fetch error:", error);
    }
  }
}

if (!customElements.get("pf-purchase-together")) {
  customElements.define("pf-purchase-together", PfPurchaseTogether);
}



// PF Trust Badges
class PFTrustBadges extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.badges.forEach(badge => {
      if(badge.dataset.scrollSelector == "null") return;

      badge.addEventListener("click", this.onClick.bind(this), { signal });
    })
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get badges() {
    return Array.from(this.querySelectorAll(".pf-tbadge-card"));
  }

  onClick(e) {
    e.preventDefault();

    const scrollEl = document.querySelector(e.currentTarget?.dataset.scrollSelector);
    if(!scrollEl) return;

    scrollEl.scrollIntoView({
      behavior: "smooth",
    });
  }
}

if (!customElements.get("pf-trust-badges")) {
  customElements.define("pf-trust-badges", PFTrustBadges);
}
